import { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Text,
  HStack,
  VStack,
  Button,
  Input,
  CircularProgress,
  useToast,
} from "@chakra-ui/react";

import { getGeneralConfig, postAllHeartbeats, postGeneralConfig } from "../../utils/api";
import {
  GlobalConfigIntFields,
  GeneralConfig,
  GrupoConfigStringFields,
} from "../../utils/interfaces";
import { AxiosError } from "axios";


export default function GeneralConfigForm({ onClose }: { onClose: () => void }): JSX.Element {
  const toast = useToast();
  const [maybeResponse, setResponse] = useState<GeneralConfig | null>(null);

  useEffect(() => {
    getGeneralConfig()
      .then(res => setResponse(res.data))
      .catch(err => console.log("Error al obtener configuración:", err));
  }, []);

  if (maybeResponse === null) {
    return (
      <VStack justifyContent="center" my="12" w="100%">
        <CircularProgress isIndeterminate size={120} color="teal" />
        <Text fontSize="2xl">Cargando configuración...</Text>
      </VStack>
    );
  }

  const { global, grupos } = maybeResponse;

  const onSubmitGeneralConfig = (event: React.FormEvent) => {
    event.preventDefault();

    /**
     * POST /semaphore/config llama a la función POSTConfig, definida en
     * semaphoreback/api/post_config.go
     */
    postGeneralConfig({ global: global, grupos: grupos })
      .then((_) => onClose())
      .catch((err: AxiosError) => {
        // Se muestra un toast con el error plasmado en errorMessage.
        let errorMessage = "";
        if (err.response) {
          // Si el backend respondió con un error (código no 2xx):
          errorMessage = err.response.data as string;
        } else if (err.request) {
          // Si hubo petición, pero no respuesta del servidor:
          errorMessage = "Error de conexión con el servidor."
        } else {
          // Si algo más salió mal:
          errorMessage = err.message;
        }
        toast({
          title: "Error",
          description: errorMessage,
          status: "error",
          duration: 9000,
          isClosable: true,
        })
      });
  };

  const onSubmitAllHeartbeats = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const newHeartbeat = parseInt((event.currentTarget[0] as HTMLInputElement).value);
    postAllHeartbeats(newHeartbeat)
      .then(_ => toast({
        title: "Éxito",
        description: "¡Heartbeats cambiados exitosamente!",
        status: "success",
        duration: 9000,
        isClosable: true,
      }))
      .catch((err: AxiosError) => {
        // Se muestra un toast con el error plasmado en errorMessage.
        let errorMessage = "";
        if (err.response) {
          // Si el backend respondió con un error (código no 2xx):
          errorMessage = err.response.data as string;
        } else if (err.request) {
          // Si hubo petición, pero no respuesta del servidor:
          errorMessage = "Error de conexión con el servidor."
        } else {
          // Si algo más salió mal:
          errorMessage = err.message;
        }
        toast({
          title: "Error",
          description: errorMessage,
          status: "error",
          duration: 9000,
          isClosable: true,
        })
      });
  }

  // Función necesaria para desactivar el scrolling en los campos numéricos
  // Por defecto el scrolling cambia el valor en esos campos, lo cual es molesto
  // Esta función desactiva eso
  const disableScroll = (event: React.WheelEvent<HTMLInputElement>) => {
    (event.target as HTMLElement).blur();
  };

  /*
    Nota para todas las funciones onChange:
    event.target se refiere al input en sí (Input, TimeInput, Radio)
    event.target.name es el parámetro name pasado al input
    event.target.value es el valor actual almacenado por el input
    En el caso de Select, se debe usar event.target.selectedOptions para acceder
    a las opciones disponibles a seleccionar
  */

  const onChangeInt = (event: React.ChangeEvent<HTMLInputElement>) => {
    global[event.target.name as keyof GlobalConfigIntFields] = parseInt(event.target.value);
  };

  // const onChangeFloat = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   global[event.target.name as keyof GlobalConfigFloatFields] = parseFloat(event.target.value);
  // };

  //   const onChangeBoolean = (event: React.ChangeEvent<HTMLInputElement>) => {
  //     localConfig[event.target.name as keyof SemaphoreConfigBooleanFields] = event.target.value === "true";
  //   }

  const onChangeGroupString = (event: React.ChangeEvent<HTMLInputElement>) => {
    const [key, groupID] = event.target.name.split("-");
    const i = grupos.findIndex(grupo => grupo.id === parseInt(groupID));
    grupos[i][key as keyof GrupoConfigStringFields] = event.target.value;
  };

  /* Funciones auxiliares para evitar repetir tanto código */

  const MyHeading = ({ heading }: { heading: string }): JSX.Element => {
    return (
      <Heading as="h3" size="md" py={4}>
        {heading}
      </Heading>
    );
  };

  const MyLabel = ({ label }: { label: string }): JSX.Element => {
    return (
      <Box w="40%">
        <Text>
          <b>{label}</b>
        </Text>
      </Box>
    );
  };

  const IntGlobalInput = ({ label, name }: { label: string; name: keyof GlobalConfigIntFields }): JSX.Element => {
    return (
      <HStack>
        <MyLabel label={label} />
        <Input
          name={name}
          defaultValue={global[name]}
          placeholder="0"
          onChange={onChangeInt}
          onWheel={disableScroll}
          type="number"
          step="1"
          variant="flushed"
          htmlSize={40}
          width="60%"
        />
      </HStack>
    );
  };

  const StringGroupInput = ({ label, groupID, name }: { label: string; groupID: number; name: keyof GrupoConfigStringFields }): JSX.Element => {
    return (
      <HStack>
        <MyLabel label={label} />
        <Input
          name={`${name}-${groupID}`}
          defaultValue={grupos.find(grupo => grupo.id === groupID)![name]}
          onChange={onChangeGroupString}
          variant="flushed"
          htmlSize={40}
          width="60%"
        />
      </HStack>
    );
  };

  return (
    <VStack align="left">
      <MyHeading heading="Establecer nuevo heartbeat para todos los FirmPro" />
      <form onSubmit={onSubmitAllHeartbeats}>
        <VStack spacing={1}>
          <HStack>
            <MyLabel label="Nuevo heartbeat" />
            <Input
              name="heartbeat"
              defaultValue="60"
              placeholder="0"
              onChange={onChangeInt}
              onWheel={disableScroll}
              type="number"
              step="1"
              variant="flushed"
              htmlSize={40}
              width="40%"
            />
            <Button type="submit" colorScheme="pink">Guardar</Button>
          </HStack>
        </VStack>
      </form>

      <hr />

      <form onSubmit={onSubmitGeneralConfig} method="post">
        <VStack spacing={1} align="left">
          <MyHeading heading="Máximo tiempo de desconexión durante temporada para gatillar..." />
          <IntGlobalInput label="...falla leve (s)" name="max_tiempo_desconexion_temporada_falla_leve" />
          <IntGlobalInput label="...falla grave (s)" name="max_tiempo_desconexion_temporada_falla_grave" />

          {grupos.map(grupo => (
            <div key={grupo.id}>
              <hr />
              <MyHeading heading={`Grupo ${grupo.id}`} />
              <StringGroupInput label="Nombre" groupID={grupo.id} name="nombre" />
              <StringGroupInput label="Inicio de temporada (formato: día/mes)" groupID={grupo.id} name="inicio_temporada" />
              <StringGroupInput label="Fin de temporada (formato: día/mes)" groupID={grupo.id} name="fin_temporada" />
            </div>
          ))}
          <Button type="submit" shadow="lg" my={4}>
            Actualizar
          </Button>
        </VStack>
      </form>
    </VStack>
  );
}
